/** @jsxImportSource @emotion/react */
import { useTheme } from '@emotion/react';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@TextField';
import { useEffect, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import ValidationStatus from '../../Data/ValidationStatus';
import { getErrorMessage } from '../../Helpers/validation';
import {
  clearConstraintsValidations,
  setAutoCompleteInputValue,
  setComponentValidationStatus,
  updateValue,
} from '../../Lib/redux/slices/components';
import { setShouldNavigateForward } from '../../Lib/redux/slices/navigationControls';
import { getVisiblePageComponents } from '../Page/page.helpers';
import QuestionText from '../QuestionText/QuestionText';
import {
  getComponentValidationStatus,
  isEveryComponentValid,
} from '../__helpers__/component.helpers';
import {
  createAutoCompletePopper,
  isThisLastAddressComponentOnPage,
  renderMainTextWithHighlighting,
  showAllOptionsWhenNoMatches,
} from './googleAddress.helpers';
import makeLocationIconStyles from './makeLocationIconStyles';
import {
  getPlacePredictionsSaga,
  handleAnswerQuestionSideEffectsSaga,
  handleGoogleAddressSelectedSaga,
} from '../../Hooks/actionCreators';
import { useAutoFocusFirstEditableComponent } from '../../Hooks/useAutoFocusFirstEditableComponent';
import { renderMustache } from '../../Helpers/mustache';
import { getAutoCompleteName } from '../TextFieldInput/textfield.helpers';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';

/**
 * GoogleAddressInput
 *
 * This component is used to select an address from a dropdown.
 * Options are fetched from Google Places API via the gateway.
 *
 */
const GoogleAddressInput = ({ componentId, iFrameId, text, formId }) => {
  const dispatch = useDispatch();
  const { countryCode, phoneNumberCountryCode, subcategoryId } = useSelector(
    (store) => store.formConfig
  );
  const { visitorId } = useSelector((store) => store.userIdentities);
  const { pages } = useSelector((store) => store.paging);
  const component = useSelector(
    (store) => store.components[`component_${componentId}`]
  );
  const mappedQuestionId = component?.component?.mappedQuestionId;
  const [userHasSelectedOption, setUserHasSelectedOption] = useState(false);
  const { currentPage } = useSelector((store) => store.navigation);
  const components = useSelector((store) => store.components);
  const { dynamicNewsletterName } = useSelector((store) => store.newsletter);

  const errorMessage = getErrorMessage(component.validation?.constraints);
  const {
    options,
    inputValue,
    placeholder,
    restrictFromCountry,
    useLocationBias,
    shouldPopulateUpcomingFields,
  } = component.input;
  const theme = useTheme();
  const locationIconStyles = makeLocationIconStyles({ theme });
  const componentStatus = getComponentValidationStatus({ component });
  const isAnswered =
    componentStatus === ValidationStatus.VALID ||
    componentStatus === ValidationStatus.INVALID;

  const textfieldExperimentName = useGetExperimentName({
    componentId,
    componentName: 'TextField',
  });
  useEffect(() => {
    /**
     * Attempt Navigation: on blur of the GoogleAddressInputValue if all components on this page are valid and answered
     */
    const pageComponentIds = pages[currentPage];
    const visiblePageComponents = getVisiblePageComponents(
      pageComponentIds,
      components,
      dynamicNewsletterName,
      phoneNumberCountryCode,
      subcategoryId
    );

    if (
      isEveryComponentValid({
        components: visiblePageComponents,
        countryCode,
      }) &&
      componentStatus === ValidationStatus.VALID &&
      userHasSelectedOption &&
      isThisLastAddressComponentOnPage({ components, componentId })
    ) {
      setUserHasSelectedOption(false);
      dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
    }
  }, [
    currentPage,
    pages,
    components,
    countryCode,
    dispatch,
    componentStatus,
    componentId,
    userHasSelectedOption,
  ]);

  const handleInputChange = (event, newInputValue) => {
    if (event && event.type === 'click') {
      // i.e. if the user has selected an option from the dropdown not changed the input value
      const newInputValueInOptions = options.some(
        (option) => option.description === newInputValue
      );

      if (newInputValueInOptions) {
        setUserHasSelectedOption(true);
      }
    }

    if (event && event.type === 'change') {
      setUserHasSelectedOption(false);

      dispatch(
        getPlacePredictionsSaga({
          componentId,
          input: newInputValue,
          visitorId,
          regionCode: restrictFromCountry
            ? countryCode.toLowerCase()
            : undefined,
          locationBias: useLocationBias ? 'headers' : undefined,
        })
      );

      batch(() => {
        dispatch(
          setAutoCompleteInputValue({ componentId, value: newInputValue })
        );
        dispatch(updateValue({ componentId, value: newInputValue }));
      });
    }
  };

  const handleChange = async (event, value) => {
    if (event.type !== 'click') {
      // if the user clicked they selected the input field, so we don't want to set as selected and thus prevent navigation. An input change is where the user has selected an option.
      setUserHasSelectedOption(false);
    }

    dispatch(
      handleGoogleAddressSelectedSaga({
        componentId,
        placeId: value.placeId,
        inputValue: value.description,
        formId,
        iFrameId,
        visitorId,
        shouldPopulateUpcomingFields,
      })
    );
  };

  const handleBlur = (event) => {
    event.preventDefault();
    if (inputValue === '' || inputValue === undefined) {
      return;
    }
    setUserHasSelectedOption(false);
    batch(() => {
      dispatch(setAutoCompleteInputValue({ componentId, value: inputValue }));
      dispatch(updateValue({ componentId, value: inputValue }));
      dispatch(setComponentValidationStatus({ componentId, iFrameId, formId }));
      dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
    });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const autoCompleteRef = useRef();
  useAutoFocusFirstEditableComponent({
    componentId,
    inputRef: autoCompleteRef,
  });

  const renderAutocompleteOption = (props, option) => {
    return (
      <li {...props}>
        <Grid container alignItems="center">
          <Grid item>
            <LocationOnIcon css={locationIconStyles.icon} />
          </Grid>
          <Grid item xs>
            {renderMainTextWithHighlighting(option)}

            <Typography variant="body2" color="textSecondary">
              {option?.secondaryText?.text || ''}
            </Typography>
          </Grid>
        </Grid>
      </li>
    );
  };

  const renderInput = (params) => (
    <TextField
      inputProps={{
        ...params.inputProps,
        autoComplete: getAutoCompleteName(null, mappedQuestionId),
      }}
      inputRef={params.InputProps.ref}
      fullWidth
      type="text"
      placeholder={placeholder}
      errorMessage={errorMessage}
      onClick={() => {
        setUserHasSelectedOption(false);
        dispatch(clearConstraintsValidations({ componentId }));
      }}
      isAnswered={isAnswered}
      iconType="locationService"
      experimentName={textfieldExperimentName}
    />
  );

  return (
    <>
      <QuestionText
        heading={renderMustache({
          template: text,
          components,
          currentComponent: component,
          iFrameId,
          formId,
        })}
      />
      <Box
        marginTop={theme.mvf.spacing.medium}
        data-testid="googleAddressContainer"
      >
        <Autocomplete
          freeSolo
          disableClearable
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : option.description
          }
          autoComplete
          id={`google-address-${componentId}`}
          inputValue={inputValue || ''}
          onInputChange={handleInputChange}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          options={options || []}
          isOptionEqualToValue={(option, value) => option === value}
          onBlur={handleBlur}
          PopperComponent={createAutoCompletePopper(autoCompleteRef)}
          ref={autoCompleteRef}
          renderInput={renderInput}
          renderOption={renderAutocompleteOption}
          filterOptions={showAllOptionsWhenNoMatches}
        />
      </Box>
    </>
  );
};

export default GoogleAddressInput;
